// src/pages/teacher/TeacherAttandance.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Badge, Spinner, Alert
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  collection, doc, setDoc, onSnapshot, query, where,
  serverTimestamp, getDoc, getDocs, limit
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";

/* ────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────── */
const toU = (s) => String(s || "").trim().toUpperCase();

// DO NOT split classes on whitespace; only commas / pipes / slashes.
const parseClassList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map(s=>s.trim()).filter(Boolean);
  return String(raw).split(/[,\|/]+/g).map(s=>s.trim()).filter(Boolean);
};

// generic list (safe for sections: whitespace is fine)
const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map((x) => x.trim()).filter(Boolean);
  return String(raw).split(/[,\s/|]+/g).map((x) => x.trim()).filter(Boolean);
};

// Prefer classIds like Assignments page; fall back to classes/classNames/className
const getTeacherClasses = (t) => {
  const fromIds = parseClassList(t?.classIds);
  if (fromIds.length) return fromIds;
  const fromClasses = parseClassList(t?.classes || t?.classNames);
  if (fromClasses.length) return fromClasses;
  return t?.className ? [String(t.className).trim()] : [];
};

const getSectionsForClass = (t, cls) => {
  if (!t) return [];
  if (t.sectionsByClass && t.sectionsByClass[cls]) {
    return parseList(t.sectionsByClass[cls]).map(toU);
  }
  const flat = parseList(t.sections).map(toU);
  if (flat.length) return flat;
  const single = parseList(t.section).map(toU);
  if (single.length) return single;
  return [];
};

const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmtTime = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("DD MMM, hh:mm A") : "—");
const clean = (s = "") => String(s).trim().replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
const norm = (v = "") => String(v).toLowerCase().replace(/\s+/g, " ").trim();
const makeAttId = (date, className, section, studentId) =>
  `${dayjs(date).format("YYYYMMDD")}_${clean(className)}_${clean(section)}_${studentId}`;
const normalizeStatusInput = (k = "") => {
  const v = String(k || "").toLowerCase().trim();
  if (v === "leave" || v === "leaves") return "excused";
  return v;
};

const STATUSES = [
  { key: "present", label: "Present", code: "P", variant: "success" },
  { key: "absent", label: "Absent", code: "A", variant: "danger" },
  { key: "late", label: "Late", code: "L", variant: "warning" },
  { key: "excused", label: "Excused / Leave", code: "E", variant: "info" },
  { key: "halfday", label: "Half-day", code: "H", variant: "secondary" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.key, s]));

/* ────────────────────────────────────────────────────────────
   Component
   ──────────────────────────────────────────────────────────── */
export default function TeacherAttandance() {
  const { user, profile } = useAuth();
  const schoolId = profile?.schoolId || null;

  /* Teacher record */
  const [teacher, setTeacher] = useState(null);
  const [loadingTeacher, setLoadingTeacher] = useState(true);
  const [teacherErr, setTeacherErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingTeacher(true);
        setTeacherErr("");
        if (!schoolId || !user?.email) {
          setTeacher(null);
          setTeacherErr("Missing school/account.");
          setLoadingTeacher(false);
          return;
        }

        if (profile?.teacherId) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", profile.teacherId));
          if (alive && snap.exists()) {
            setTeacher({ id: snap.id, ...(snap.data() || {}) });
            setLoadingTeacher(false);
            return;
          }
        }

        const qs = await getDocs(
          query(
            collection(db, "schools", schoolId, "teachers"),
            where("email", "==", (user.email || "").toLowerCase()),
            limit(1)
          )
        );
        if (alive) {
          if (!qs.empty) setTeacher({ id: qs.docs[0].id, ...(qs.docs[0].data() || {}) });
          else setTeacherErr("Teacher record not found for this account.");
          setLoadingTeacher(false);
        }
      } catch {
        if (alive) {
          setTeacher(null);
          setTeacherErr("Failed to load teacher profile.");
          setLoadingTeacher(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [schoolId, user?.email, profile?.teacherId]);

  const teacherName = teacher?.name || user?.displayName || user?.email || "Teacher";
  const teacherBranch = (teacher?.branch || "").trim();

  /* Class + Section (single selects; like Assignments) */
  const teacherClasses = getTeacherClasses(teacher);
  const [className, setClassName] = useState("");
  const [sectionsAvail, setSectionsAvail] = useState([]);
  const [section, setSection] = useState(""); // "" => All Sections

  // Initialize selects
  useEffect(() => {
    const first = teacherClasses[0] || "";
    setClassName(first);
    const secs = getSectionsForClass(teacher, first);
    setSectionsAvail(secs);
    setSection(""); // All by default
  }, [teacher?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update sections on class change
  useEffect(() => {
    const secs = getSectionsForClass(teacher, className);
    setSectionsAvail(secs);
    if (secs.length && !secs.includes(toU(section))) setSection("");
  }, [className, teacher]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Mode / filters */
  const [mode, setMode] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [qText, setQText] = useState("");
  const [branch, setBranch] = useState("");

  /* Students */
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);

  useEffect(() => {
    if (!schoolId || !className) {
      setStudents([]); setLoadingStudents(false); return;
    }
    setLoadingStudents(true);
    let cancelled = false;
    const col = collection(db, "schools", schoolId, "students");

    const qRef = section
      ? query(col, where("className", "==", className), where("section", "==", section))
      : query(col, where("className", "==", className));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        if (cancelled) return;
        const list = snap.docs
          .map((d) => {
            const s = d.data() || {};
            return {
              id: d.id,
              name: s.name || s.displayName || "—",
              rollNo: s.rollNo || s.studentId || "",
              photoURL: s.photoURL || "",
              className: s.className || "",
              section: s.section || "",
              branch: s.branch || s.stream || "",
              status: s.status || "active",
              lastAttendanceStatus: s.lastAttendanceStatus || "",
              lastAttendanceDate: toDate(s.lastAttendanceDate),
            };
          })
          .filter((s) => !branch || toU(s.branch) === toU(branch))
          .filter((s) => norm(s.status) !== "inactive")
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setStudents(list);
        setLoadingStudents(false);
      },
      () => setLoadingStudents(false)
    );

    return () => { cancelled = true; try { unsub(); } catch {} };
  }, [schoolId, className, section, branch]);

  /* Branch options */
  const branchOptions = useMemo(() => {
    const set = new Set(
      students.map((s) => (s.branch || "").toString().trim()).filter(Boolean).map((b) => b.toUpperCase())
    );
    return Array.from(set).sort();
  }, [students]);

  useEffect(() => {
    if (!branch && teacherBranch && branchOptions.includes(teacherBranch.toUpperCase())) {
      setBranch(teacherBranch.toUpperCase());
    }
  }, [branch, teacherBranch, branchOptions]);

  /* Daily attendance snapshot */
  const [attDaily, setAttDaily] = useState(new Map());
  useEffect(() => {
    if (!schoolId || !className || mode !== "daily") return;

    const start = dayjs(selectedDate).startOf("day").toDate();
    const end   = dayjs(selectedDate).endOf("day").toDate();
    const col = collection(db, "schools", schoolId, "attendance");

    const qRef = section
      ? query(col,
          where("className", "==", className),
          where("section", "==", section),
          where("date", ">=", start),
          where("date", "<=", end)
        )
      : query(col,
          where("className", "==", className),
          where("date", ">=", start),
          where("date", "<=", end)
        );

    const unsub = onSnapshot(qRef, (snap) => {
      const map = new Map();
      snap.forEach((d) => {
        const x = d.data() || {};
        if (!x.studentId) return;
        map.set(x.studentId, { id: d.id, ...x });
      });
      setAttDaily(map);
    });
    return () => unsub();
  }, [schoolId, className, section, selectedDate, mode]);

  /* Monthly attendance snapshot */
  const [attMonthly, setAttMonthly] = useState([]);
  useEffect(() => {
    if (!schoolId || !className || mode !== "monthly") return;

    const mStart = dayjs(selectedMonth + "-01").startOf("month").toDate();
    const mEnd   = dayjs(selectedMonth + "-01").startOf("month").add(1, "month").toDate();

    const col = collection(db, "schools", schoolId, "attendance");
    const qRef = section
      ? query(col,
          where("className", "==", className),
          where("section", "==", section),
          where("date", ">=", mStart),
          where("date", "<", mEnd)
        )
      : query(col,
          where("className", "==", className),
          where("date", ">=", mStart),
          where("date", "<", mEnd)
        );

    const unsub = onSnapshot(qRef, (snap) => {
      setAttMonthly(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    });
    return () => unsub();
  }, [schoolId, className, section, selectedMonth, mode]);

  /* Filtering + selection + drafts */
  const filteredStudents = useMemo(() => {
    const q = qText.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      `${s.name} ${s.rollNo}`.toLowerCase().includes(q)
    );
  }, [students, qText]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  useEffect(() => { setSelectedIds(new Set()); }, [className, section, branch, selectedDate, selectedMonth, mode]);

  const [draftStatus, setDraftStatus] = useState(new Map());
  useEffect(() => { setDraftStatus(new Map()); }, [className, section, branch, selectedDate, selectedMonth, mode]);

  const getEffectiveStatus = (id) =>
    (draftStatus.has(id) ? draftStatus.get(id) : (attDaily.get(id)?.status || ""));
  const hasPending = (id) =>
    draftStatus.has(id) && draftStatus.get(id) !== (attDaily.get(id)?.status || "");
  const setDraft = (id, value) =>
    setDraftStatus((prev) => { const n = new Map(prev); n.set(id, value); return n; });
  const clearDraft = (ids) =>
    setDraftStatus((prev) => { const n = new Map(prev); (ids || Array.from(n.keys())).forEach((i) => n.delete(i)); return n; });

  const selectedCount = selectedIds.size;
  const allVisibleSelected = selectedCount > 0 && filteredStudents.every((s) => selectedIds.has(s.id));
  const toggleSelectOne = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAllVisible = () => setSelectedIds((prev) => {
    if (allVisibleSelected) { const n = new Set(prev); filteredStudents.forEach((s) => n.delete(s.id)); return n; }
    const n = new Set(prev); filteredStudents.forEach((s) => n.add(s.id)); return n;
  });
  const clearSelection = () => setSelectedIds(new Set());

  /* Write helpers */
  const currentDateObj = useMemo(() => dayjs(selectedDate).toDate(), [selectedDate]);

  const mirrorToStudent = async (studentId, status) => {
    if (!schoolId || !studentId) return;
    await setDoc(
      doc(db, "schools", schoolId, "students", studentId),
      { lastAttendanceStatus: status || "", lastAttendanceDate: currentDateObj },
      { merge: true }
    );
  };

  const waitForAttendanceEcho = (studentId, expectedStatus, timeoutMs = 5000) =>
    new Promise((resolve) => {
      if (!schoolId) return resolve(false);
      const stu = students.find((s) => s.id === studentId);
      const attId = makeAttId(currentDateObj, stu?.className || "", stu?.section || "", studentId);
      const ref = doc(db, "schools", schoolId, "attendance", attId);
      let done = false;
      const off = onSnapshot(
        ref,
        (snap) => {
          const d = snap.data();
          if (d?.status === expectedStatus) {
            done = true; off(); resolve(true);
          }
        },
        () => { if (!done) { done = true; resolve(false); } }
      );
      setTimeout(() => { if (!done) { done = true; off(); resolve(false); } }, timeoutMs);
    });

  const isTeacherInactive = (teacher, profile) => {
    const s = String(teacher?.status ?? profile?.status ?? "").trim().toLowerCase();
    const hasInactiveFlag =
      typeof teacher?.active === "boolean" ? !teacher.active
      : typeof profile?.active === "boolean" ? !profile.active
      : false;
    return hasInactiveFlag || ["inactive", "disabled", "blocked"].includes(s);
  };
  const inactive = isTeacherInactive(teacher, profile);

  const writeOne = async ({ student, status, note }) => {
    if (!schoolId || inactive) return;
    const secForThisStudent = student.section || "";
    const clsForThisStudent = student.className || "";
    const id = makeAttId(currentDateObj, clsForThisStudent, secForThisStudent, student.id);
    const normalized = normalizeStatusInput(status);
    const ref = doc(db, "schools", schoolId, "attendance", id);

    const existingSnap = await getDoc(ref);
    const firstTime = !existingSnap.exists();

    // optimistic UI
    setAttDaily((prev) => {
      const next = new Map(prev);
      const ex = next.get(student.id) || {};
      next.set(student.id, {
        ...ex,
        id,
        studentId: student.id,
        status: normalized,
        note: note ?? ex.note ?? "",
        updatedAt: new Date(),
        markedByName: teacherName || "",
      });
      return next;
    });

    const payload = {
      schoolId,
      date: currentDateObj,
      className: clsForThisStudent,
      section: secForThisStudent,
      branch: student.branch || "",
      studentId: student.id,
      rollNo: String(student.rollNo || ""),
      status: normalized,
      note: note ?? (existingSnap.data()?.note || ""),
      markedBy: user?.uid || "",
      markedByName: teacherName || "",
      updatedAt: serverTimestamp(),
      ...(firstTime ? { createdAt: serverTimestamp(), locked: true } : {}),
    };

    await setDoc(ref, payload, { merge: true });
    await mirrorToStudent(student.id, normalized);
  };

  /* Bulk ops */
  const bulkSetStatus = (status) => {
    if (!selectedCount) return;
    const normalized = normalizeStatusInput(status);
    setDraftStatus((prev) => {
      const n = new Map(prev);
      students.filter((s) => selectedIds.has(s.id)).forEach((stu) => n.set(stu.id, normalized));
      return n;
    });
  };

  const copyYesterdaySelectedDraft = async () => {
    if (!selectedCount) return;

    const yStart = dayjs(selectedDate).subtract(1, "day").startOf("day").toDate();
    const yEnd   = dayjs(selectedDate).subtract(1, "day").endOf("day").toDate();

    const col = collection(db, "schools", schoolId, "attendance");
    const qRef = section
      ? query(col, where("className", "==", className), where("section", "==", section), where("date", ">=", yStart), where("date", "<=", yEnd))
      : query(col, where("className", "==", className), where("date", ">=", yStart), where("date", "<=", yEnd));

    const snap = await getDocs(qRef);
    const byStudent = new Map();
    snap.forEach((d) => { const x = d.data() || {}; if (x.studentId) byStudent.set(x.studentId, x); });

    if (!byStudent.size) {
      return Swal.fire({ icon: "info", title: "No data from yesterday to copy" });
    }
    setDraftStatus((prev) => {
      const n = new Map(prev);
      students.filter((s) => selectedIds.has(s.id)).forEach((stu) => {
        const y = byStudent.get(stu.id);
        const st = normalizeStatusInput(y?.status || "present");
        n.set(stu.id, st);
      });
      return n;
    });
  };

  const [savingBulk, setSavingBulk] = useState(false);
  const updateSelected = async () => {
    const idsToSave = Array.from(selectedIds).filter((id) => draftStatus.has(id));
    if (!idsToSave.length) return;
    try {
      setSavingBulk(true);
      Swal.fire({ title: "Updating selected…", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const ops = idsToSave.map(async (id) => {
        const student = students.find((s) => s.id === id);
        const status = normalizeStatusInput(draftStatus.get(id));
        if (!student || status === undefined) return false;
        await writeOne({ student, status });
        const echoed = await waitForAttendanceEcho(student.id, status);
        return echoed;
      });

      const results = await Promise.allSettled(ops);
      const success = results.filter((r) => r.status === "fulfilled" && r.value === true).length;
      const fail = results.length - success;

      clearDraft(idsToSave);
      Swal.close();

      await Swal.fire({
        icon: fail ? "warning" : "success",
        title: fail ? "Partially synced" : "Updated",
        text: fail
          ? `${success} synced, ${fail} pending/failed. View may update as Firestore syncs.`
          : `${success} student${success > 1 ? "s" : ""} updated & synced.`,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Bulk update failed", text: err?.message || "Unexpected error" });
    } finally {
      setSavingBulk(false);
    }
  };

  /* Derived totals + monthly aggregation */
  const dailyTotals = useMemo(() => {
    const totals = { present: 0, absent: 0, late: 0, excused: 0, halfday: 0, unmarked: 0 };
    filteredStudents.forEach((s) => {
      const st = attDaily.get(s.id)?.status || "";
      if (!st) totals.unmarked += 1; else totals[st] = (totals[st] || 0) + 1;
    });
    return totals;
  }, [filteredStudents, attDaily]);

  const monthlyAgg = useMemo(() => {
    if (mode !== "monthly") return { rows: [], days: [] };
    const daysInMonth = dayjs(selectedMonth + "-01").daysInMonth();
    const dayKeys = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const byStudent = new Map();
    students.forEach((s) => {
      byStudent.set(s.id, {
        student: s,
        days: Object.fromEntries(dayKeys.map((d) => [d, ""])),
        totals: { P: 0, A: 0, L: 0, E: 0, H: 0 },
      });
    });

    const codeByStatus = { present: "P", absent: "A", late: "L", excused: "E", halfday: "H" };
    attMonthly.forEach((rec) => {
      const sid = rec.studentId; const date = toDate(rec.date);
      if (!sid || !date) return;
      const row = byStudent.get(sid); if (!row) return;
      const d = dayjs(date).date();
      const code = codeByStatus[rec.status] || "";
      if (!row.days[d]) row.days[d] = code;
    });

    byStudent.forEach((row) => {
      dayKeys.forEach((d) => {
        const c = row.days[d];
        if (c) row.totals[c] = (row.totals[c] || 0) + 1;
      });
    });

    return { rows: Array.from(byStudent.values()), days: dayKeys };
  }, [mode, selectedMonth, students, attMonthly]);

  /* CSV Export */
  const secTag = section || "ALL";
  const exportDailyCSV = () => {
    if (!filteredStudents.length) return;
    const rows = filteredStudents.map((s) => {
      const rec = attDaily.get(s.id) || {};
      return {
        Name: s.name,
        Class: s.className || "",
        Section: s.section || "",
        RollNo: s.rollNo || "",
        Branch: s.branch || "",
        Status: STATUS_MAP[rec.status]?.label || "",
        UpdatedAt: rec.updatedAt ? dayjs(rec.updatedAt).format("YYYY-MM-DD HH:mm") : "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DailyAttendance");
    XLSX.writeFile(wb, `attendance_${clean(className)}_${clean(secTag)}_${selectedDate}.xlsx`);
  };

  const exportMonthlyCSV = () => {
    if (!monthlyAgg.rows.length) return;
    const rows = monthlyAgg.rows
      .filter((r) => filteredStudents.some((s) => s.id === r.student.id))
      .map((r) => ({
        Name: r.student.name,
        Class: r.student.className || "",
        Section: r.student.section || "",
        RollNo: r.student.rollNo || "",
        Branch: r.student.branch || "",
        ...monthlyAgg.days.reduce((acc, d) => { acc[`Day ${d}`] = r.days[d] || ""; return acc; }, {}),
        P: r.totals.P, A: r.totals.A, L: r.totals.L, E: r.totals.E, H: r.totals.H,
      }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MonthlyAttendance");
    XLSX.writeFile(wb, `attendance_${clean(className)}_${clean(secTag)}_${selectedMonth}.xlsx`);
  };

  // inactive short-circuit
  useEffect(() => {
    if (inactive) { setStudents([]); setLoadingStudents(false); }
  }, [inactive]);

  /* Render */
  if (loadingTeacher) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading profile…
        </Card.Body>
      </Card>
    );
  }
  if (!loadingTeacher && teacher && inactive) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger" className="mb-3">
          <div className="fw-semibold mb-1">Access restricted</div>
          Your teacher account is marked as <b>Inactive</b>. Please contact the administrator to regain access.
        </Alert>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => window.location.assign("/")}>Go Home</Button>
          <Button variant="outline-secondary" onClick={() => window.location.assign("/support")}>Contact Support</Button>
        </div>
      </Container>
    );
  }
  if (teacherErr) return <Alert variant="warning" className="mb-0">{teacherErr}</Alert>;
  if (!teacher || !className) {
    return (
      <Alert variant="warning" className="mb-0">
        Your teacher record doesn’t have a <b>className</b> (and optional <b>section</b>). Ask the school admin to update it.
      </Alert>
    );
  }

  return (
    <Container fluid className="p-0 position-relative">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Attendance</h5>
          <small className="text-muted">
            {teacherName} • Class {className} • {section ? `Section ${section}` : "All Sections"} • {filteredStudents.length}/{students.length} students
          </small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant={mode === "daily" ? "primary" : "outline-primary"} onClick={() => setMode("daily")}>Daily</Button>
            <Button variant={mode === "monthly" ? "primary" : "outline-primary"} onClick={() => setMode("monthly")}>Monthly</Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="p-3 mb-3 shadow-sm rounded-4">
        <Row className="g-2 align-items-end">
          {/* Class (single) */}
          <Col md={3} sm={6} xs={12}>
            <Form.Label className="mb-1">Class</Form.Label>
            <Form.Select value={className} onChange={(e)=>setClassName(e.target.value)}>
              {teacherClasses.map((c) => (
                <option key={c} value={c}>{`Class ${c}`}</option>
              ))}
            </Form.Select>
          </Col>

          {/* Section (single, with All Sections) */}
          <Col md={3} sm={6} xs={12}>
            <Form.Label className="mb-1">Section</Form.Label>
            <Form.Select value={section} onChange={(e)=>setSection(toU(e.target.value))}>
              <option value="">All Sections</option>
              {sectionsAvail.map((s) => <option key={s} value={s}>{`Section ${s}`}</option>)}
            </Form.Select>
          </Col>

          {mode === "daily" ? (
            <Col md={3} sm={6} xs={12}>
              <Form.Label className="mb-1">Date</Form.Label>
              <Form.Control type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            </Col>
          ) : (
            <Col md={3} sm={6} xs={12}>
              <Form.Label className="mb-1">Month</Form.Label>
              <Form.Control type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
            </Col>
          )}

          <Col md={3} sm={6} xs={12}>
            <Form.Label className="mb-1">Branch</Form.Label>
            <Form.Select value={branch} onChange={(e) => setBranch(e.target.value)}>
              <option value="">All</option>
              {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </Form.Select>
          </Col>

          <Col md={6} sm={12} xs={12}>
            <Form.Label className="mb-1">Search student</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control placeholder="Name or Roll No." value={qText} onChange={(e) => setQText(e.target.value)} />
              {qText && <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>}
            </InputGroup>
          </Col>
        </Row>

        {mode === "daily" && (
          <>
            <Row className="mt-3 g-2 align-items-center">
              <Col className="d-flex flex-wrap gap-2">
                <Badge bg="success">Present: {dailyTotals.present}</Badge>
                <Badge bg="danger">Absent: {dailyTotals.absent}</Badge>
                <Badge bg="warning" text="dark">Late: {dailyTotals.late}</Badge>
                <Badge bg="info" text="dark">Excused: {dailyTotals.excused}</Badge>
                <Badge bg="secondary">Half-day: {dailyTotals.halfday}</Badge>
                <Badge bg="light" text="dark">Unmarked: {dailyTotals.unmarked}</Badge>
              </Col>
              <Col className="text-end">
                <ButtonGroup>
                  <Button variant="outline-secondary" size="sm" onClick={exportDailyCSV}>
                    <i className="bi bi-download me-1" /> Export CSV
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            {/* Bulk actions */}
            <Row className="mt-2 g-2 align-items-center">
              <Col className="d-flex flex-wrap gap-2 align-items-center">
                <Badge bg="dark">Selected: {selectedCount}</Badge>
                <Button size="sm" variant="outline-success" onClick={() => bulkSetStatus("present")}><i className="bi bi-check2-circle me-1" />Present</Button>
                <Button size="sm" variant="outline-danger" onClick={() => bulkSetStatus("absent")}><i className="bi bi-x-circle me-1" />Absent</Button>
                <Button size="sm" variant="outline-warning" onClick={() => bulkSetStatus("late")}><i className="bi bi-clock me-1" />Late</Button>
                <Button size="sm" variant="outline-info" onClick={() => bulkSetStatus("excused")}><i className="bi bi-info-circle me-1" />Excused/Leave</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => bulkSetStatus("halfday")}><i className="bi bi-calendar2-day me-1" />Half-day</Button>
                <Button size="sm" variant="outline-primary" onClick={copyYesterdaySelectedDraft}><i className="bi bi-copy me-1" />Copy Yesterday (Draft)</Button>
                <Button size="sm" variant="primary" onClick={updateSelected} disabled={!Array.from(selectedIds).some((id) => draftStatus.has(id))}>
                  {savingBulk ? (<><Spinner animation="border" size="sm" className="me-1" /> Updating…</>) : (<><i className="bi bi-upload me-1" /> Update Selected</>)}
                </Button>
                <Button size="sm" variant="outline-dark" onClick={clearSelection}><i className="bi bi-x-lg me-1" />Clear Selection</Button>
              </Col>
            </Row>

            <div className="small text-muted mt-2">
              Legend: <Badge bg="success">P</Badge> Present · <Badge bg="danger">A</Badge> Absent · <Badge bg="warning" text="dark">L</Badge> Late · <Badge bg="info" text="dark">E</Badge> Excused/Leave · <Badge bg="secondary">H</Badge> Half-day
            </div>
          </>
        )}

        {mode === "monthly" && (
          <Row className="mt-3 g-2 align-items-center">
            <Col className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={exportMonthlyCSV}>
                <i className="bi bi-download me-1" /> Export CSV
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      {/* Daily table */}
      {mode === "daily" && (
        <Card className="p-3 shadow-sm rounded-4">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="sticky-top bg-body">
                <tr>
                  <th style={{ width: 36 }}>
                    <Form.Check
                      type="checkbox"
                      checked={filteredStudents.length > 0 && allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible students"
                    />
                  </th>
                  <th style={{ minWidth: 220 }}>Student</th>
                  <th className="d-none d-md-table-cell">Roll No.</th>
                  <th className="d-none d-lg-table-cell">Branch</th>
                  <th>Status (draft)</th>
                  <th className="d-none d-xl-table-cell">Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loadingStudents ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <Spinner animation="border" size="sm" /> Loading…
                    </td>
                  </tr>
                ) : filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">No students found.</td>
                  </tr>
                ) : (
                  filteredStudents.map((s) => {
                    const rec = attDaily.get(s.id);
                    const checked = selectedIds.has(s.id);
                    return (
                      <tr key={s.id} className={hasPending(s.id) ? "table-warning" : ""}>
                        <td className="sticky-col col-0">
                          <Form.Check
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelectOne(s.id)}
                            aria-label={`Select ${s.name}`}
                          />
                        </td>
                        <td className="sticky-col col-1">
                          <div className="fw-semibold text-truncate text-muted" style={{ maxWidth: 260 }}>
                            {s.name}
                            {hasPending(s.id) && <span className="ms-2 badge bg-warning text-dark">unsaved</span>}
                          </div>
                          <div className="text-muted small">Section: {s.section || "—"}</div>
                          {s.lastAttendanceStatus ? (
                            <div className="small mt-1">
                              <Badge bg="light" text="dark" className="me-1">Last</Badge>
                              <Badge bg={STATUS_MAP[s.lastAttendanceStatus]?.variant || "secondary"}>
                                {STATUS_MAP[s.lastAttendanceStatus]?.code || "—"}
                              </Badge>
                            </div>
                          ) : null}
                        </td>
                        <td className="d-none d-md-table-cell text-muted">{s.rollNo || "—"}</td>
                        <td className="d-none d-lg-table-cell text-muted">{s.branch || "—"}</td>
                        <td style={{ width: 320 }}>
                          <div className="d-flex gap-2 align-items-center flex-wrap text-muted">
                            <StatusSelect
                              value={getEffectiveStatus(s.id)}
                              onChange={(v) => setDraft(s.id, normalizeStatusInput(v))}
                              disabled={false}
                            />
                            <div className="btn-group btn-group-sm" role="group" aria-label="Quick status">
                              <Button variant="outline-success" onClick={() => setDraft(s.id, "present")}>P</Button>
                              <Button variant="outline-danger"  onClick={() => setDraft(s.id, "absent")}>A</Button>
                              <Button variant="outline-warning" onClick={() => setDraft(s.id, "late")}>L</Button>
                              <Button variant="outline-info"    onClick={() => setDraft(s.id, "excused")}>E</Button>
                              <Button variant="outline-secondary" onClick={() => setDraft(s.id, "halfday")}>H</Button>
                            </div>
                          </div>
                        </td>
                        <td className="d-none d-xl-table-cell text-muted">{fmtTime(rec?.updatedAt)}</td>
                        <td style={{ width: 160 }}>
                          <Button
                            className="text-muted"
                            size="sm"
                            variant={hasPending(s.id) ? "primary" : "outline-secondary"}
                            disabled={!hasPending(s.id)}
                            onClick={async () => {
                              const status = normalizeStatusInput(draftStatus.get(s.id));
                              try {
                                Swal.fire({ title: `Saving ${s.name}…`, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                                await writeOne({ student: s, status });
                                const echoed = await waitForAttendanceEcho(s.id, status);
                                Swal.close();
                                clearDraft([s.id]);
                                await Swal.fire({
                                  icon: echoed ? "success" : "info",
                                  title: echoed ? "Updated" : "Updated (sync pending)",
                                  timer: 1100,
                                  showConfirmButton: false,
                                });
                              } catch (err) {
                                Swal.close();
                                await Swal.fire({ icon: "error", title: "Update failed", text: err?.message || "Write failed" });
                              }
                            }}
                          >
                            <i className="bi bi-upload me-1 " />
                            Update
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* Monthly cards */}
      {mode === "monthly" && (
        <Card className="p-3 shadow-sm rounded-4">
          <Row className="g-2 align-items-center mb-2">
            <Col>
              <div className="text-muted small">Month: {dayjs(selectedMonth + "-01").format("MMMM YYYY")}</div>
            </Col>
            <Col className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={exportMonthlyCSV}>
                <i className="bi bi-download me-1" /> Export CSV
              </Button>
            </Col>
          </Row>

          <div className="month-grid">
            {loadingStudents ? (
              <div className="py-5 text-center text-muted">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : monthlyAgg.rows
                .filter((r) => filteredStudents.some((s) => s.id === r.student.id))
                .map((r) => (
                  <Card key={r.student.id} className="month-card shadow-sm">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="fw-semibold text-truncate">{r.student.name || "—"}</div>
                          <div className="small text-muted">
                            <span className="me-3">Roll: {r.student.rollNo || "—"}</span>
                            <span>Branch: {r.student.branch || "—"}</span>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-1 text-nowrap">
                          <Badge bg="success">P {r.totals.P}</Badge>
                          <Badge bg="danger">A {r.totals.A}</Badge>
                          <Badge bg="warning" text="dark">L {r.totals.L}</Badge>
                          <Badge bg="info" text="dark">E {r.totals.E}</Badge>
                          <Badge bg="secondary">H {r.totals.H}</Badge>
                        </div>
                      </div>

                      <div className="day-strip" role="list" aria-label="Attendance by day">
                        {monthlyAgg.days.map((d) => {
                          const dateObj = dayjs(selectedMonth + `-${String(d).padStart(2, "0")}`);
                          const dayName = dateObj.format("ddd");
                          return (
                            <div role="listitem" key={d} className="day-chip">
                              <div className="day-num">{d}</div>
                              <div className="day-name">{dayName}</div>
                              <div className="day-code">
                                <StatusBadge code={r.days[d]} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
          </div>

          <div className="small text-muted mt-3">
            Legend: <Badge bg="success">P</Badge> Present · <Badge bg="danger">A</Badge> Absent ·
            <Badge bg="warning" text="dark" className="ms-1">L</Badge> Late ·
            <Badge bg="info" text="dark" className="ms-1">E</Badge> Excused/Leave ·
            <Badge bg="secondary" className="ms-1">H</Badge> Half-day
          </div>
        </Card>
      )}

      {/* Styles */}
      <style>{`
        .card { border-radius: 16px; }
        .form-control, .form-select { border-radius: 12px; }
        .btn { border-radius: 10px; }
        .table > :not(caption) > * > * { vertical-align: middle; }
        tr.table-warning { --bs-table-bg: color-mix(in oklab, var(--bs-warning-bg, #fff3cd) 45%, transparent) !important; }

        .month-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 14px; }
        .month-card { border-radius: 14px; }

        .day-strip { display: grid; grid-auto-flow: column; grid-auto-columns: 52px; gap: 8px; overflow-x: auto; padding-bottom: 6px; scrollbar-gutter: stable both-edges; }
        .day-chip { display: grid; grid-template-rows: auto auto auto; align-items: center; justify-items: center; border: 1px solid var(--bs-border-color); border-radius: 10px; padding: 4px 2px; min-height: 72px; background: color-mix(in oklab, var(--bs-body-bg, #fff) 98%, transparent); }
        .day-num { font-size: 12px; font-weight: 600; line-height: 1; margin-bottom: 2px; }
        .day-name { font-size: 11px; color: var(--bs-secondary-color); margin-bottom: 4px; }
        .day-code .badge { width: 28px; display: inline-flex; align-items: center; justify-content: center; }

        .day-strip::-webkit-scrollbar { height: 8px; }
        .day-strip::-webkit-scrollbar-thumb { background: color-mix(in oklab, var(--bs-secondary-color, #adb5bd) 40%, transparent); border-radius: 8px; }
        .day-strip::-webkit-scrollbar-track { background: color-mix(in oklab, var(--bs-body-bg, #fff) 90%, transparent); }
        @media (max-width: 480px) {
          .month-grid { grid-template-columns: 1fr; }
          .day-strip { grid-auto-columns: 46px; }
        }
      `}</style>
    </Container>
  );
}

/* Small inline components */
function StatusSelect({ value, onChange, disabled }) {
  return (
    <Form.Select size="sm" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>
      {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
    </Form.Select>
  );
}
function StatusBadge({ code }) {
  const map = { "": "secondary", P: "success", A: "danger", L: "warning", E: "info", H: "secondary" };
  const text = code || "—";
  return <Badge bg={map[code] || "secondary"}>{text}</Badge>;
}
